import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./MenuPage.css";

const API_ROOT =
  window.location.hostname === "localhost"
    ? "http://localhost/restaurant_api"
    : "https://menu-rtl.ir/restaurant_api";

function buildUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_ROOT}/${path.replace(/^\/+/, "")}`;
}

export default function MenuPage() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState("fa");

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const table = params.get("table") || "";

  const CART_KEY = "cart";

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    setCart(saved);
    const savedLang = localStorage.getItem("menu_language") || "fa";
    setLanguage(savedLang);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    fetch(`${API_ROOT}/menu_api.php`, {
      signal: controller.signal,
      headers: { Accept: "application/json", "Cache-Control": "no-cache" },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Server error ${response.status}: ${errText || ""}`);
        }
        const text = await response.text();
        if (!text || text.trim() === "") throw new Error("Empty response");
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error("Invalid JSON: " + e.message);
        }
      })
      .then((json) => {
        if (json && json.success) {
          // show only visible categories/items to customers
          setCategories(
            (json.categories || []).filter((c) => parseInt(c.is_hidden) === 0)
          );
          setMenuItems(
            (json.menu || []).filter((m) => parseInt(m.is_hidden) === 0)
          );
        } else {
          setError(json.error || json.message || "Invalid response");
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        if (err.name === "AbortError") setError("Request timeout");
        else setError(err.message || "Network error");
        if (process.env.NODE_ENV === "development") {
          setCategories([
            { id: 1, name: "Main", name_fa: "اصلی", image: "", is_hidden: 0 },
          ]);
          setMenuItems([
            {
              id: 1,
              category_id: 1,
              name: "Sample",
              name_fa: "نمونه",
              description: "",
              description_fa: "",
              price: 10000,
              image: "",
              is_hidden: 0,
            },
          ]);
        }
      })
      .finally(() => {
        setLoading(false);
        clearTimeout(timeoutId);
      });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  const saveCart = (next) => {
    setCart(next);
    localStorage.setItem(CART_KEY, JSON.stringify(next));
  };
  const addToCart = (item) => {
    const next = [...cart];
    const idx = next.findIndex((i) => i.id === item.id);
    if (idx > -1) next[idx].quantity++;
    else next.push({ ...item, quantity: 1 });
    saveCart(next);
  };
  const removeOne = (item) => {
    const next = [...cart];
    const idx = next.findIndex((i) => i.id === item.id);
    if (idx > -1) {
      if (next[idx].quantity <= 1) next.splice(idx, 1);
      else next[idx].quantity--;
    }
    saveCart(next);
  };
  const qtyOf = (id) => cart.find((i) => i.id == id)?.quantity || 0;

  const toggleLanguage = () => {
    const newLang = language === "fa" ? "en" : "fa";
    setLanguage(newLang);
    localStorage.setItem("menu_language", newLang);
  };

  const getText = (item, field) => {
    const fa =
      item[`${field}_fa`] && item[`${field}_fa`].trim() !== ""
        ? item[`${field}_fa`]
        : null;
    const en = item[field] && item[field].trim() !== "" ? item[field] : null;
    if (language === "fa") return fa || en || "";
    return en || fa || "";
  };

  const filtered = selectedCategoryId
    ? menuItems.filter(
        (i) => String(i.category_id) === String(selectedCategoryId)
      )
    : [];
  const total = cart.reduce(
    (s, i) => s + (i.price || 0) * (i.quantity || 0),
    0
  );

  if (loading)
    return (
      <div className="dm-menu-page container">
        <div className="logo-loader">
          <img src="/logo512.png" alt="logo" />
        </div>
      </div>
    );
  if (error)
    return (
      <div className="dm-menu-page container">
        <div className="error-container">
          <h3>{language === "fa" ? "خطا در بارگذاری منو" : "Loading error"}</h3>
          <p>{error}</p>
          <div>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                window.location.reload();
              }}
            >
              {language === "fa" ? "تلاش مجدد" : "Retry"}
            </button>
          </div>
          <div>
            <small>API: {API_ROOT}/menu_api.php</small>
          </div>
        </div>
      </div>
    );

  return (
    <div className="dm-menu-page container">
      <div className="dm-fixed-header">
        <div className="header-content header-3col">
          {/* راست */}
          <button className="dm-lang-btn" onClick={toggleLanguage}>
            {language === "fa" ? "EN" : "FA"}
          </button>

          {/* وسط */}
          <div className="header-logo">
            <img src="/logo192.png" alt="logo" />
          </div>

          {/* چپ */}
          <span className="header-title">
            {language === "fa" ? "منوی رستوران" : "Restaurant Menu"}
          </span>
        </div>
      </div>

      <div style={{ height: 70 }}></div>

      {!selectedCategoryId && (
        <section className="dm-categories">
          {categories.length === 0 ? (
            <div className="empty-categories">
              <p>
                {language === "fa" ? "هیچ دسته‌ای وجود ندارد" : "No categories"}
              </p>
            </div>
          ) : (
            categories.map((cat) => (
              <div
                key={cat.id}
                className="dm-category-card"
                onClick={() => setSelectedCategoryId(cat.id)}
              >
                <img
                  src={buildUrl(cat.image)}
                  alt={getText(cat, "name")}
                  className="dm-category-img"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                <div
                  className="category-placeholder"
                  style={{ display: "none" }}
                >
                  🍽️
                </div>
                <h3>{getText(cat, "name")}</h3>
              </div>
            ))
          )}
        </section>
      )}

      {selectedCategoryId && (
        <>
          <div className="dm-fixed-back-btn-container">
            <button
              className="dm-fixed-back-btn"
              onClick={() => setSelectedCategoryId(null)}
            >
              {language === "fa" ? "← بازگشت" : "← Back"}
            </button>
          </div>

          <div style={{ height: 60 }}></div>

          <section className="dm-menu-items">
            {filtered.length === 0 ? (
              <div className="empty-items">
                <p>
                  {language === "fa"
                    ? "هیچ آیتمی در این دسته نیست"
                    : "No items"}
                </p>
              </div>
            ) : (
              filtered.map((item) => (
                <div key={item.id} className="dm-item-card">
                  <img
                    src={buildUrl(item.image)}
                    alt={getText(item, "name")}
                    className="dm-item-img"
                    onError={(e) =>
                      (e.currentTarget.src =
                        "https://via.placeholder.com/150?text=No+Image")
                    }
                  />
                  <div className="dm-item-body">
                    <h4>{getText(item, "name")}</h4>
                    <p>
                      {getText(item, "description") ||
                        (language === "fa" ? "بدون توضیحات" : "No description")}
                    </p>
                    <div className="dm-item-bottom">
                      <span className="dm-price">
                        {Number(item.price).toLocaleString()}{" "}
                        {language === "fa" ? "تومان" : "Toman"}
                      </span>
                      <div className="dm-qty">
                        <button onClick={() => removeOne(item)}>-</button>
                        <span>{qtyOf(item.id)}</span>
                        <button onClick={() => addToCart(item)}>+</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </section>
        </>
      )}

      {cart.length > 0 && (
        <div className="dm-cart-bar">
          <div className="dm-cart-left">
            {language === "fa" ? "مجموع:" : "Total:"} {total.toLocaleString()}{" "}
            {language === "fa" ? "تومان" : "Toman"}
          </div>
          <button
            className="dm-cart-button"
            onClick={() => navigate(`/cart?table=${table}`)}
          >
            🛒 {language === "fa" ? "سبد خرید" : "Cart"} (
            {cart.reduce((s, i) => s + i.quantity, 0)})
          </button>
        </div>
      )}
    </div>
  );
}
